import {
  CalendarDate,
  CalendarDateTime,
  ZonedDateTime
} from "@internationalized/date";
import { DataFieldProps } from "atom/datafield/dataFieldComponents";
import { Cubit } from "blac-next";
import { error, warn } from "lib/log";
import { requestIdleCallback } from "lib/requestIdleCallback";
import React, { ReactNode } from "react";
import { DateValue } from "react-aria-components";
import { z } from "zod";

import { DataFieldDateProps } from "molecule/datafield/DataFieldDate";

import { CheckboxGroupProps } from "molecule/checkbox/CheckboxGroup";
import { DatePickerProps } from "molecule/datepicker/DatePicker";
import { DateRangePickerProps } from "molecule/daterangepicker/DateRangePicker";
import { DateRange, Key } from "react-aria";

export type FormSchemaType = z.ZodObject<z.ZodRawShape>;
export type FormError = z.ZodIssue | undefined;

export type FormKey<S extends FormSchemaType> = keyof z.infer<S>;
export type FormValueType<S extends FormSchemaType> = z.infer<S>[FormKey<S>];

export type FormValues<S extends FormSchemaType> = z.infer<S>;
export type FormValuesPartial<S extends FormSchemaType> = Partial<
  FormValues<S>
>;

export type FormErrors<S extends FormSchemaType> = Partial<
  Record<FormKey<S>, FormError[]>
>;

export type ValidationError = string | string[];
export type ValidationErrors = Record<string, ValidationError>;

export type AutoFormBlocState<S extends FormSchemaType> = {
  dirty: boolean;
  values: FormValuesPartial<S>;
  errors: FormErrors<S>;
  schema?: S;
  validationErrors?: ValidationErrors;
};

export type AutoFormRegisterProps<T = DataFieldProps> = {
  name: string;
  defaultValue: T extends DataFieldDateProps
    ? DateValue
    : T extends DateRangePickerProps<DateValue>
      ? DateRangePickerProps<DateValue>["defaultValue"]
      : T extends DatePickerProps<CalendarDate | CalendarDateTime>
        ? DatePickerProps<CalendarDate | CalendarDateTime>["defaultValue"]
        : T extends CheckboxGroupProps
          ? string[]
          : string;
  onChangeCapture?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  errorParser?: (props: T) => string;
  error?: string | z.ZodIssue;
  isInvalid?: boolean;
  required?: boolean;
  onDataValid?: (valid: boolean) => void;
};

export type FieldPropsFn<S extends FormSchemaType> = (
  name: FormKey<S>,
  options?: FieldPropsOptions
) => AutoFormRegisterProps;

export type AutoFormControlsActions<S extends FormSchemaType> = {
  getValues: () => FormValuesPartial<S>;
  setValue: (name: FormKey<S>, value: FormValueType<S>) => void;
  setValues: (values: Partial<FormValuesPartial<S>>) => void;
  setError: (name: FormKey<S>, error: string | undefined) => void;
  reset: () => void;
  dirty: boolean;
};

export interface FormFieldBaseState {
  setValue: (value: string) => void;
}

export interface DropdownState {
  setValue: (value: Key | null) => void;
}

export interface AutocompleteState {
  setValue: (value: Key) => void;
}

export interface DateFieldState {
  setValue: (value: DateValue) => void;
}

export interface CheckboxGroupState {
  setValue: (value: string[]) => void;
}

export interface DateRangePickerState {
  setValue: (value: DateRange) => void;
}

export type FormFieldState =
  | FormFieldBaseState
  | DateFieldState
  | DropdownState
  | CheckboxGroupState
  | DateRangePickerState;

export type AutoFormProps<S extends FormSchemaType> = {
  schema: S;
  onSubmit?: (
    data: FormValues<S>,
    controls: AutoFormControlsActions<S>
  ) => void;
  children?: ReactNode;
  initialValue?: FormValuesPartial<S>;
  errorParser?: (props: DataFieldProps) => string;
  validateOnChange?: boolean;
  onChange?: (
    key: FormKey<S>,
    value: FormValueType<S>,
    form: {
      data: FormValuesPartial<S>;
      errors: FormErrors<S>;
      controls: AutoFormControlsActions<S>;
    }
  ) => void;
  contextId?: string;
};

interface FieldPropsOptions {
  type?:
    | typeof Number
    | typeof String
    | typeof ZonedDateTime
    | typeof Array<string>;
  ref?: { current: FormFieldState | null };
}

export type AutoFormValidateOptions = {
  focusInputOnError?: boolean;
  formEl?: HTMLFormElement | null;
};

export default class AutoFormBloc<S extends FormSchemaType> extends Cubit<
  AutoFormBlocState<S>,
  AutoFormProps<S>
> {
  constructor() {
    super({ values: {}, errors: {}, dirty: false });
  }

  registeredFields: Set<string> = new Set();
  fieldTypes: Partial<Record<FormKey<S>, FieldPropsOptions["type"]>> = {};
  fieldRefs: Partial<Record<FormKey<S>, FieldPropsOptions["ref"]>> = {};

  registerAutoFormField: FieldPropsFn<S> = (key, options = {}) => {
    const { type = String, ref } = options;
    const name = String(key);
    this.fieldTypes[key] = type;
    this.fieldRefs[key] = ref;
    const initialValue = this.props?.initialValue ?? {};
    const defaultValue = (initialValue as Record<string, string>)[name] ?? "";

    const rtProps = {
      name,
      defaultValue,
      errorParser: this.props?.errorParser
    } satisfies AutoFormRegisterProps;

    this.patch({ values: { ...this.state.values, [name]: defaultValue } });

    if (!this.props?.schema) {
      return rtProps;
    }

    const fieldSchema = this.props.schema.shape[name];

    if (!fieldSchema) {
      warn(`AutoForm: schema does not contain field with the name: "${name}"`, {
        props: this.props,
        name
      });
      return {} as AutoFormRegisterProps;
    }

    this.registeredFields.add(name);
    return rtProps;
  };

  unregisterAutoFormField = (name: string) => {
    this.registeredFields.delete(name);
    this.patch({ values: { ...this.state.values, [name]: undefined } });
  };

  // TODO: Change argument to schema
  setSchema = (props: AutoFormProps<S>) => {
    if (props.schema && this.state.schema !== props.schema) {
      this.patch({ schema: props.schema });
      requestIdleCallback(() => this.checkForm());
    }
  };

  removeEmptyStrings = (data: FormValuesPartial<S>): FormValuesPartial<S> => {
    const cleanedData = { ...data };
    for (const key in cleanedData) {
      const emptyString = data[key] === "";
      const emptyArray =
        Array.isArray(cleanedData[key]) && cleanedData[key].length === 0;
      if (emptyString || emptyArray) {
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete cleanedData[key];
      }
    }
    return cleanedData;
  };

  parseDataFieldTypes = (data: FormValuesPartial<S>): FormValuesPartial<S> => {
    const parsedData = { ...data };
    for (const key in parsedData) {
      const type = this.fieldTypes[key as FormKey<S>];
      if (type === Number) {
        parsedData[key] = Number(parsedData[key]) as FormValueType<S>;
      }
    }
    return parsedData;
  };

  gatherFormData = (
    options: {
      removeEmptyStrings?: boolean;
    } = {}
  ): FormValuesPartial<S> => {
    let data = { ...this.state.values };
    if (options.removeEmptyStrings) {
      data = this.removeEmptyStrings(data);
    }

    data = this.parseDataFieldTypes(data);

    return data;
  };

  validateFormInputs = (options: AutoFormValidateOptions = {}): boolean => {
    const { focusInputOnError = false, formEl = null } = options;

    if (Object.keys(this.state.errors).length > 0) {
      this.patch({ errors: {}, validationErrors: {} });
    }
    const data = this.gatherFormData({
      removeEmptyStrings: true
    });
    try {
      this.state.schema?.parse(data);
      return true;
    } catch (e) {
      this.checkForm({ err: true });
      let errors: typeof this.state.errors = {};
      const validationErrors: ValidationErrors = {};

      if (e instanceof z.ZodError) {
        warn("AutoForm: form submission failed", {
          data,
          issues: e.issues
        });
        for (const issue of e.issues) {
          const simplePath = issue.path.filter((p) => typeof p === "string");
          const path = simplePath.join(".");
          const current: FormError[] =
            this.state.errors[path as FormKey<S>] ?? [];
          const newIssues = issue;

          if (current[0]?.code !== newIssues.code) {
            errors = { ...errors, [path]: [issue] };
            validationErrors[path] = issue.message;
          }
        }

        this.patch({
          errors,
          validationErrors
        });

        if (focusInputOnError && formEl && Object.keys(errors).length > 0) {
          const firstKey = Object.keys(errors)[0];
          const input = formEl.elements.namedItem(firstKey);
          setTimeout(() => {
            if (input && "focus" in input && input instanceof HTMLElement) {
              input.focus();
            }
          }, 60);
        }
      }
    }
    return false;
  };

  handleSubmit = (
    event: React.FormEvent<HTMLFormElement>,
    validateOptions?: AutoFormValidateOptions
  ) => {
    event.preventDefault();
    const isValid = this.validateFormInputs(validateOptions);
    const data = this.gatherFormData();
    if (isValid) {
      this.props?.onSubmit?.(data, this.formControls);
    }
  };

  checkForm = (
    options: {
      err?: boolean;
    } = {}
  ) => {
    const notify = options.err ? error : warn;
    const missingFields = Object.keys(this.state.schema?.shape ?? {}).filter(
      (name) => {
        const shape = this.state.schema?.shape[name];
        const isRequired = !shape?.isOptional() && !shape?.isNullable();

        if (!isRequired) {
          return false;
        }

        const missing = this.registeredFields.has(name);
        return !missing;
      }
    );

    if (missingFields.length > 0) {
      notify(
        `AutoForm: schema contains required fields that are not registered: ${missingFields.join(
          ", "
        )}`
      );
    }
  };

  handleChange = (key: FormKey<S>, value: FormValueType<S>) => {
    const currentValues = this.state.values[key];
    if (currentValues === value) {
      return;
    }

    const data = { ...this.state.values, [key]: value } as FormValues<S>;

    this.patch({
      values: data
    });

    setTimeout(() => {
      this.props?.onChange?.(key, value, {
        data: this.state.values,
        errors: this.state.errors,
        controls: this.formControls
      });
    });
  };

  getValues = (): typeof this.state.values => {
    return this.state.values;
  };

  get formControls(): AutoFormControlsActions<S> {
    return {
      getValues: this.getValues,
      setValue: this.setValue,
      setValues: this.setValues,
      setError: this.setError,
      reset: this.reset,
      dirty: this.state.dirty
    } satisfies AutoFormControlsActions<S>;
  }

  setValue = <K extends FormKey<S>>(name: K, value: FormValues<S>[K]) => {
    try {
      const currentValue = this.state.values[name];
      if (currentValue === value) {
        return;
      }

      const newValues = { ...this.state.values, [name]: value };

      this.patch({
        values: newValues,
        dirty: true
      });

      const ref = this.fieldRefs[name];

      if (ref?.current) {
        const state = ref.current;
        state.setValue(value);
      }
    } catch (e) {
      error("AutoFormBloc.setValue error", { name, value, error: e });
    }
  };

  setValues = (values: Partial<typeof this.state.values>) => {
    try {
      const newValues = { ...this.state.values, ...values };

      this.patch({
        values: newValues,
        dirty: true
      });

      for (const [key, value] of Object.entries(values)) {
        const ref = this.fieldRefs[key as FormKey<S>];

        // A falsy-but-valid value (0, "", false) must still reach the
        // field's visual state — only skip a genuinely absent one.
        if (ref && ref.current && value !== undefined) {
          const state = ref.current;
          state.setValue(value as FormValueType<S>);
        }
      }
    } catch (e) {
      error("AutoFormBloc.setValues error", { values, error: e });
    }
  };

  setError = (name: FormKey<S>, errorMessage: string | undefined) => {
    const newValidationErrors = { ...this.state.validationErrors };
    if (errorMessage) {
      newValidationErrors[name as string] = errorMessage;
    } else {
      if (typeof newValidationErrors[name as string] !== "undefined") {
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete newValidationErrors[name as string];
      }
    }
    this.patch({ validationErrors: newValidationErrors });
  };

  reset = () => {
    this.patch({
      values: this.props?.initialValue ?? {},
      errors: {},
      validationErrors: {},
      dirty: false
    });

    for (const [key] of Object.entries(this.state.values)) {
      const ref = this.fieldRefs[key as FormKey<S>];

      if (ref && ref.current) {
        const state = ref.current;
        const val = this.props?.initialValue?.[key as FormKey<S>];
        // A falsy-but-valid default (0, "", false) must still reach the
        // field's visual state — only skip a genuinely absent one.
        if (val !== undefined) {
          state.setValue(val);
        }
      }
    }
  };
}
